import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import Stats from '@alltrails/core/types/Stats';
import { FormattedMessage } from 'react-intl';
import { Key } from 'react';
import Shroom from '@alltrails/denali/illustrations/Shroom';
import Footprints from '@alltrails/denali/illustrations/Footprints';
import { formatLoggedDistance } from '@alltrails/landing-pages';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import Heart from '@alltrails/denali/icons/Heart';
import signSrc from '../../assets/images/highlights/sign.svg';
import onePercentSrc from '../../assets/images/highlights/one-percent.svg';
import starReviewsSrc from '../../assets/images/highlights/star-reviews.svg';
import styles from './styles/styles.module.scss';

const highlights = (languageRegionCode: LanguageRegionCode) => [
  {
    description: (
      <FormattedMessage defaultMessage="AllTrails is the most trusted and used outdoor platform in the world and is frequently ranked as a Top 5 Health and Fitness app." />
    ),
    heading: <FormattedMessage defaultMessage="Over 1 million 5-star reviews" />,
    icon: starReviewsSrc,
    isSVG: true
  },
  {
    description: (
      <FormattedMessage
        defaultMessage="We're home to the largest collection of curated trails with more than {curatedTrails}K trails worldwide, and {loggedDistance} billion miles of trails."
        values={{ curatedTrails: Stats.NumCuratedTrails, loggedDistance: formatLoggedDistance(languageRegionCode) }}
      />
    ),
    heading: <FormattedMessage defaultMessage="Over {curatedTrails}K+ trails worldwide" values={{ curatedTrails: Stats.NumCuratedTrails }} />,
    icon: signSrc, // different from @alltrails/denali/illustrations/Sign
    isSVG: true
  },
  {
    description: (
      <FormattedMessage defaultMessage="We help people explore the outdoors safely by keeping them on the right path with trail maps, on-trail navigation, and status updates to loved ones." />
    ),
    heading: <FormattedMessage defaultMessage="On-trail safety features" />,
    icon: <Footprints color="currentcolor" size="xs" />
  },
  {
    description: (
      <FormattedMessage defaultMessage="AllTrails is available in three tiers. Base membership is free. Plus unlocks offline exploration and more. Peak offers our best features for personalized adventures." />
    ),
    heading: <FormattedMessage defaultMessage="Elevate your adventure" />,
    icon: <Heart color="--color-text-primary" />
  },
  {
    description: (
      <FormattedMessage defaultMessage="Millions of people around the world trust us as their one-stop shop for trail navigation and outdoor community connection." />
    ),
    heading: <FormattedMessage defaultMessage="Community of {fellowExplorers}+ million people" values={{ fellowExplorers: Stats.NumUsers }} />,
    icon: <Shroom color="currentcolor" size="xs" />
  },
  {
    description: (
      <FormattedMessage defaultMessage="As proud 1% for the Planet partners, we contribute part of our annual revenue to organizations that protect and increase equitable access to the outdoors." />
    ),
    heading: <FormattedMessage defaultMessage="We put our money where the trails are" />,
    icon: onePercentSrc,
    isSVG: true
  }
];

// Desktop: https://www.figma.com/file/XrlDM2ZdWXmlnl7ZXUicGn/Press%2FAbout%2F404?node-id=1701%3A10880&t=eYPxUX4Ha19x4ECZ-4
// Mobile: https://www.figma.com/file/XrlDM2ZdWXmlnl7ZXUicGn/Press%2FAbout%2F404?node-id=1701-11386&t=3iCzUOrB1ngnoqZo-4
export default function Hightlights() {
  const languageRegionCode = useLanguageRegionCode();

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography component="h2" variant="heading500" className={styles.header}>
          <FormattedMessage defaultMessage="Highlights" />
        </Typography>
        <Typography className={styles.paragraph} component="div" variant="paragraph300">
          <FormattedMessage defaultMessage="Each day, we’re dedicated to growing our offerings to make AllTrails even better for the people we serve." />
        </Typography>
        <div className={styles.highlights}>
          {highlights(languageRegionCode).map((highlight, index) => (
            <div key={index as Key}>
              {highlight?.isSVG ? <Image alt="" src={highlight.icon} loading="lazy" /> : highlight.icon}
              <Typography className={styles.subHeader} component="h3" variant="heading400">
                {highlight.heading}
              </Typography>
              <Typography variant="paragraph200">{highlight.description}</Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
